import React, {
	useCallback,
	useState,
	SyntheticEvent,
	useMemo,
	useEffect,
} from "react";
import {
	Button,
	Reference,
	SearchSelectOptionProps,
} from "@alm/flow-ds/components";
import { ElementComponentProps } from "elements/types";
import { State } from "data";
import {
	createGetElementData,
	createGetElementListData,
	ElementComponentData,
	ListElementComponentData,
	prepSearchConfig,
} from "data/form/elementSelectors";
import { useDispatch, useSelector } from "react-redux";
import { ElementData, ReferenceElement } from "records/Element";
import { useStandardElement } from "components/Forms/Element";
import { DisplayOnlyValue } from "elements";
import { useTranslation } from "react-i18next";
import useOpenLinkedForm from "hooks/useOpenLinkedForm";
import _ from "lodash/fp";
import Finder from "components/Finder/Finder";

import jsonpointer from "lib/jsonpointer/jsonpointer";
import api from "lib/api";
import { QueryClause } from "lib/api/types";
import { createAlertAction } from "data/app/appActions";
import { createAlert } from "records/Alert";

function ReferenceReactive({
	fid,
	vid,
	id,
	index,
	rowIndex,
	uuid,
	hint,
	label,
	required,
	disabled,
	displayOnly,
	link,
	name,
	limit,
	isArray,
	search,
	createLink,
	updateLink,
}: ElementComponentProps<ReferenceElement>) {
	const { t } = useTranslation();
	const [finderOpen, setFinderOpen] = useState(false);
	const dispatch = useDispatch();

	const { setDataAndQueue, remove } = useStandardElement({
		fid,
		vid,
		id,
		index,
		rowIndex,
		uuid,
	});

	const getData = useMemo(createGetElementData, []);
	const getListData = useMemo(createGetElementListData, []);

	const selectorProps = useMemo(() => {
		return { uuid, fid, vid, index, rowIndex, id };
	}, [uuid, fid, vid, index, rowIndex, id]);

	const searchConfig = useSelector((state: State) =>
		prepSearchConfig(state, selectorProps, search)
	);

	const data = useSelector((ReduxState: State) =>
		isArray
			? (getListData(
					ReduxState,
					selectorProps
			  ) as ListElementComponentData<string>)
			: (getData(
					ReduxState,
					selectorProps
			  ) as ElementComponentData<string>)
	);

	const toggleFinder = useCallback(
		() => setFinderOpen(!finderOpen),
		[finderOpen]
	);

	const handleAddValues = useCallback(
		({ values }: { values: string[] }) => {
			let value: string | string[] = [];

			if (isArray && values && values.length > 0) {
				let valuesToAdd = values;
				const currentValue =
					data.value && Array.isArray(data.value) ? data.value : [];

				// Check for a limit and if we're over it
				// If we are we just take as many as the limit will allow from the incoming
				// values and add them
				if (
					limit !== undefined &&
					currentValue.length + values.length > limit
				) {
					const canAdd = limit - currentValue.length;
					valuesToAdd = values.slice(0, canAdd);
				}

				value = [...currentValue, ...valuesToAdd];
			}
			// If we're working with single values, check the incoming is not null
			// and just take the first value from it
			else if (values && values.length > 0) {
				value = values[0];
			}

			setDataAndQueue(data, { id: data.id, value });
		},
		[data, isArray, limit, setDataAndQueue]
	);

	const onClearClick = useCallback(
		(
			event:
				| SyntheticEvent<HTMLButtonElement>
				| { currentTarget: { value: string } }
		) => {
			if (isArray) {
				if (data.value && Array.isArray(data.value)) {
					const index = data.value?.indexOf(
						event.currentTarget.value
					);
					if (index === 0 && data.value?.length === 1) {
						remove();
					} else if (index !== -1) {
						const update: ElementData<string[]> = {
							id: data.id,
							value: _.pullAt(index, data.value),
						};
						setDataAndQueue(data, update);
					}
				} else {
					// If not already an array then init as empty array
					const update: ElementData<string[]> = {
						id: data.id,
						value: [],
					};
					setDataAndQueue(data, update);
				}
			} else {
				remove();
			}
		},
		[isArray, data, setDataAndQueue, remove]
	);

	const { openLinkedForm } = useOpenLinkedForm(rowIndex, name);

	const onValueClick = useCallback(
		(value, index) => {
			openLinkedForm(index);
		},
		[openLinkedForm]
	);

	// HACK: This code fixes bls issue where it can't query to cdp. Remove this if bls gets updated
	// Run to query the cdp and get value for field if needed
	const loadInitialValue = async () => {
		// Create our query payload
		const payload = _.cloneDeep(searchConfig.payload);
		const pointerToSearch = jsonpointer.pointerToId(searchConfig.label);
		const filterQuery: QueryClause = [
			{ AND: [{ [pointerToSearch]: { exact: { value: data.value } } }] },
		];
		payload.filter.AND = [...filterQuery];
		try {
			const resp = await api.cursors.create(payload);
			if (resp.results.length > 0) {
				handleAddValues({ values: [resp.results[0].id] });
			} else {
				// If cdp query is empty then clear value
				onClearClick({ currentTarget: { value: "" } });
			}
		} catch (err) {
			console.error(err);
			dispatch(
				createAlertAction(
					createAlert({
						title: t("alerts.blsCursorError.title"),
						description: t("alerts.blsCursorError.description", {
							element: label,
						}),
						status: "error",
					})
				)
			);
		}
	};

	useEffect(() => {
		if (
			// TODO: check this in tests when new testing branch is put through
			process.env.NODE_ENV !== "test" &&
			data !== undefined &&
			data !== null &&
			data.value !== undefined &&
			data.value !== null &&
			!_.isArray(data.value) &&
			data.value !== "" &&
			!(data as SearchSelectOptionProps).value.includes("cdp:/")
		) {
			loadInitialValue();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Display only
	if (displayOnly) {
		let val;

		// Multi value
		if ("items" in data) {
			val = data.items.map(item => item.label || item.value);
		}
		// Single value
		else {
			val = data.label || data.value || "";
		}

		return (
			<DisplayOnlyValue
				index={!isArray ? index : undefined}
				label={label}
				value={val}
				elem="reference"
				link={
					updateLink
						? updateLink
						: link && !updateLink && !createLink
						? link
						: undefined
				}
				onValueClick={onValueClick}
				onValueClickLabel={t("main.labels.editRecord")}
			/>
		);
	}

	return (
		<>
			<Reference
				browseBtnLabel={t("main.labels.add")}
				label={label}
				browseDisabled={
					isArray && limit ? data.value?.length === limit : false
				}
				disabled={disabled}
				value={"items" in data ? data.items : data.label}
				hint={hint}
				feedback={data.feedback}
				required={required}
				status={data.status}
				onBrowseClick={toggleFinder}
				onClearBtnClick={onClearClick}
				onValueClickLabel={t("main.labels.editRecord")}
				onValueClick={
					updateLink || (link && !createLink && !updateLink)
						? onValueClick
						: undefined
				}
				limit={
					limit && isArray
						? t("main.text.limit", { limit: limit.toString() })
						: undefined
				}
				footerButtons={
					createLink || (link && !createLink && !updateLink) ? (
						<Button
							aria-label={t("main.labels.create") + " - " + label}
							icon="plus"
							label={t("main.labels.create")}
							className={`btn-outline-primary ${
								disabled ? "disabled" : ""
							}`}
							onClick={() => openLinkedForm(index, true)}
							disabled={disabled}
						/>
					) : null
				}
			/>
			{finderOpen && (
				<Finder
					{...searchConfig}
					onCloseClick={toggleFinder}
					onAddSelected={handleAddValues}
					title={label}
					selectLimit={isArray ? limit : 1}
				/>
			)}
		</>
	);
}

export default React.memo(ReferenceReactive);
